import React, { useContext, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

import Button from '@eig-builder/core-ui/Button'
import Grid from '@eig-builder/core-ui/Grid'
import Select from '@eig-builder/control-select'
import TextField from '@eig-builder/core-ui/TextField'

import { showSuccess } from '@eig-builder/module-notification-bar-with-context/actions'
import { NotificationBarContext } from '@eig-builder/module-notification-bar-with-context'

import usePostAsJson from '@eig-builder/core-utils/hooks/usePostAsJson'

import ButtonWithLoader from 'core/components/button-with-loader'
import Modal from 'core/components/modal'

import { DataElementLocations } from '@eig-builder/core-utils/helpers/tagging-helper'
import Typography from '@eig-builder/core-ui/Typography'
import useFetchAsJson from '@eig-builder/core-utils/hooks/useFetchAsJson'
import { getCrmApiOptions } from '../../../helpers/fetch-options-helper'

const VerticalEditModal = ({ account, handleClose, refetchVerticalData, verticalData }) => {
  const [verticalId, setVerticalId] = useState(verticalData.vertical_id)

  const { dispatch: notificationDispatch } = useContext(NotificationBarContext)

  const [updateVerticalReq, updateVertical] = usePostAsJson()
  const [verticalListReq] = useFetchAsJson(getCrmApiOptions(account, '/v1.0/vertical/list'))

  useEffect(() => {
    if (updateVerticalReq.isSuccess) {
      refetchVerticalData()
      handleClose()
      const vertical = verticalListReq.data.find(d => d.id === verticalId) || {}
      showSuccess(`The vertical has been updated to: ${vertical.name} `)(notificationDispatch)
    }
  }, [updateVerticalReq.isSuccess])

  const handleUpdate = () => {
    const postObj = { ...verticalData, vertical_id: verticalId }
    updateVertical(postObj, getCrmApiOptions(account, '/v1.0/vertical'))
  }

  const handleVerticalChange = val => setVerticalId(val)

  const verticalOptions = useMemo(() => {
    return verticalListReq.data
      ? verticalListReq.data.map(d => ({ label: d.name, value: d.id }))
      : []
  }, [verticalListReq.data])

  const actions = [
    <Button
      key='edit-vertical-cancel'
      color='secondary'
      disabled={updateVerticalReq.isLoading}
      onClick={handleClose}
      dataElementLocation={DataElementLocations.BODY}
      dataElementLabel='cst-account-details--edit-vertical--cancel'
      dataElementId='cst-account-details--edit-vertical--cancel'
    >
      Cancel
    </Button>,
    <ButtonWithLoader
      key='edit-vertical-save'
      onClick={handleUpdate}
      dataElementSuffix='account-details--edit-vertical--save'
      showLoader={updateVerticalReq.isLoading}
      text='Save'
    />
  ]

  return <>
    <Modal
      actions={actions}
      dataElementPrefix='cst-account--edit-vertical'
      disableBackdropClick
      handleClose={handleClose}
      open
      title='Customer Vertical'
    >
      <Typography variant='body1' gutterBottom>
        Choose a business vertical from the dropdown below:
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Select
            readonly={!verticalListReq.data}
            name='change-vertical'
            label='Vertical'
            options={verticalOptions}
            value={verticalId}
            onChange={handleVerticalChange}
            dataElementLocation={DataElementLocations.BODY}
            dataElementLabel='cst-account-details--edit-vertical--vertical'
            dataElementId='cst-account-details--edit-vertical--vertical'
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            disabled
            label='Category'
            value={verticalData.category}
            variant='outlined'
            dataElementLocation={DataElementLocations.BODY}
            dataElementLabel='cst-account-details--edit-vertical--category'
            dataElementId='cst-account-details--edit-vertical--category'
          />
        </Grid>
      </Grid>
    </Modal>
  </>
}

VerticalEditModal.propTypes = {
  account: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  refetchVerticalData: PropTypes.func.isRequired,
  verticalData: PropTypes.shape({
    vertical: PropTypes.string.isLoading,
    category: PropTypes.string.isLoading,
    vertical_id: PropTypes.number.isRequired
  }).isRequired
}

export default VerticalEditModal
